import type { FeatureToggles } from '@qodana/report/lib/components/App/context'

import type { LicensePlan, LinterVersion } from '../types/organization'

export type ConfigFile = {
	dataApiBaseUrl: string
	bffBaseUrl?: string
	isStagingContext?: boolean
	queryErrorAlertType?: QueryErrorAlertType
	suppressedQueryErrors?: string[]
	isOfflineLicense?: boolean
	isFeedbackFormAvailable?: boolean
	isFusraEnabled?: boolean
	defaultLicensePlan?: LicensePlan
	featureToggles?: FeatureToggles
	contributorsStateOnProjectCard?: boolean
	supportEmail?: string
	isNewsletterSubscriptionHidden?: boolean
	isApiTokenFeatureAvailable?: boolean
	isTokenManagementAvailable?: boolean
	isCreateOrganizationAvailable?: boolean
	isVCSStepSkipped?: boolean
	isBaselineUpdatePromoEnabled?: boolean
	isNewLoginAccountIdIncluded?: boolean
	isQodanaEndpointIncluded?: boolean
	qodanaEndpoint?: string
	posthogKey?: string
	posthogHost?: string
	maintenance?: [string, string, string, string] // [start showing, start maintenance, end showing, message]
	linterRestriction?: {
		startDate: string
		message: string
		minLinterVersion: LinterVersion // same fields as in BE response
	}
}

export type QueryErrorAlertType = 'ui' | 'console'

let loaderPromise: Promise<ConfigFile> | undefined

async function fetchConfig(): Promise<ConfigFile> {
	if (!window.qdConfigLoader) {
		throw new Error('qdConfigLoader is undefined')
	}

	try {
		return (await window.qdConfigLoader).json()
	} catch {
		throw new Error('An error occurred in the loading config process!')
	}
}

function ensureTrailingSlash(url: string): string {
	return url.endsWith('/') ? url : url + '/'
}

function normalizeConfig(config: ConfigFile): ConfigFile {
	return {
		...config,
		dataApiBaseUrl: ensureTrailingSlash(config.dataApiBaseUrl),
	}
}

export function loadConfig(config?: ConfigFile) {
	if (loaderPromise) {
		return loaderPromise
	}

	if (config) {
		loaderPromise = Promise.resolve(normalizeConfig(config))
		return loaderPromise
	}

	loaderPromise = fetchConfig().then(normalizeConfig)
	return loaderPromise
}

async function getConfigProp<TProp extends keyof ConfigFile>(
	propName: TProp,
): Promise<ConfigFile[TProp]> {
	const config = await getLoaderPromiseIfExists()

	return config[propName]
}

export const getDataApiBaseUrl = () => getConfigProp('dataApiBaseUrl')

export const getBffBaseUrl = () => getConfigProp('bffBaseUrl')

export async function getLoaderPromiseIfExists(): Promise<ConfigFile> {
	if (!loaderPromise) {
		throw new Error('The application config is not loaded yet')
	}

	return loaderPromise
}
